import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { SERVICES, BRAND } from "@/lib/content";
import { FAQS } from "@/lib/faqs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Services() {
  return (
    <SiteLayout
      pageTitle="Services"
      pageDescription="A full-service property practice throughout Lincolnshire — EPCs, landlord inspection reports, property condition surveys, new build snagging, defect reporting, energy efficiency advice, floor plans and roof surveys."
      pagePath="/services"
    >
      <section data-testid="services-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="overline" data-testid="services-overline">A full-service practice</div>
          <h1
            data-testid="services-headline"
            className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            Surveys, reports and inspections — <span className="italic font-light text-brand-champagne">built around your decision.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            Choose a service to see exactly what&apos;s included, who it&apos;s for, and how it helps —
            from EPCs and landlord reports to forensic snagging of new build homes.
          </p>
        </div>
      </section>

      <section data-testid="services-list" className="bg-white">
        <div className="container-narrow py-20 md:py-28 space-y-24">
          {SERVICES.map((s) => (
            <article
              key={s.slug}
              id={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className="grid grid-cols-12 gap-y-8 md:gap-x-12 scroll-mt-32"
            >
              <div className="col-span-12 md:col-span-4">
                <div className="font-serif text-5xl md:text-6xl text-brand-champagne">
                  {s.number}
                </div>
                <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                  {s.title}
                </h2>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.audience.map((a) => (
                    <span
                      key={a}
                      className="text-[10px] tracking-[0.22em] uppercase border border-brand-navy/15 text-brand-navy/70 px-3 py-1.5"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
              <div className="col-span-12 md:col-span-8 md:pl-10 md:border-l md:border-brand-navy/10">
                <p className="text-base md:text-lg text-brand-navy/75 leading-relaxed">{s.long}</p>
                <div className="mt-10">
                  <div className="overline mb-4">Key benefits</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {s.benefits.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-brand-navy/85 leading-relaxed">
                        <span className="mt-2 inline-block w-2 h-px bg-brand-champagne flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/contact"
                  data-testid={`service-detail-cta-${s.slug}`}
                  className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link"
                >
                  Enquire about this service
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section data-testid="services-faq" className="bg-brand-cream border-t border-brand-navy/10">
        <JsonLd id="faq-schema" data={FAQ_SCHEMA} />
        <div className="container-narrow py-20 md:py-28">
          <div className="grid grid-cols-12 gap-y-12 md:gap-x-12">
            <div className="col-span-12 md:col-span-4">
              <div className="overline">Common questions</div>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                Frequently asked questions.
              </h2>
              <p className="mt-6 text-sm text-brand-navy/65 leading-relaxed">
                Can&apos;t find your answer? Call {BRAND.phone} or send an enquiry —
                a straight answer is always free.
              </p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-brand-navy/10">
                    <AccordionTrigger
                      data-testid={`faq-question-${i}`}
                      className="text-left font-serif text-lg md:text-xl text-brand-navy hover:no-underline hover:text-brand-champagne py-6"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`faq-answer-${i}`}
                      className="text-sm md:text-base text-brand-navy/75 leading-relaxed pb-6"
                    >
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="services-final-cta" className="bg-brand-navy text-brand-cream">
        <div className="container-narrow py-20 md:py-28 grid grid-cols-12 gap-y-10 md:gap-x-12 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              Not sure which to choose?
            </div>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.02] tracking-tight">
              A quick call is the fastest way to know what&apos;s <span className="italic font-light text-brand-champagne">actually worth doing.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="services-cta-primary"
              className="inline-flex items-center justify-between gap-3 bg-brand-champagne text-brand-navy px-7 py-5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-champagne-soft transition-colors"
            >
              Request a Quote
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="services-cta-call"
              className="inline-flex items-center justify-between gap-3 border border-brand-cream/30 text-brand-cream px-7 py-5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-cream hover:text-brand-navy transition-colors"
            >
              Call {BRAND.phone}
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
